'use client';

// components/search/OfferPopup.tsx
// syfak "month-offer" popup. When a product participates in an offer (its
// customer price carries an `offerUrl`), the card shows "ΕΠΙΛΕΞΕ ΠΡΟΣΦΟΡΑ" which
// opens this popup. We fetch the host's own offer HTML (same-origin → login
// cookie sent) and present it as a REPLICA of syfak's OWN «Προσφορές» modal
// (founder-supplied official design, 2026-07-27):
//   • green header band, centered white bold «Προσφορές», white × at the right;
//   • one light green-tinted panel PER OFFER (stacked, host order — a product
//     can be in several offers at once; see parseOffers);
//   • the offer text rendered as their VERBATIM lines (title, «Βασική Έκπτωση»,
//     «Κλίμακες», tier lines in SOURCE order — their backend's order, unsorted —
//     and every «Έκπτωση Bonus» line, kept even at 0,00 %);
//   • the ACTION controls (their quantity stepper + "Προσθήκη στο καλάθι") are
//     kept VERBATIM so their inline onclick still calls the host's
//     prosfores.add — verified working on staging 2026-07-22. We only STYLE them.
// If parsing fails (their markup changes), we fall back to rendering the raw
// snippet. The host snippet has no styling of its own — OFFER_CSS supplies it.
//
// Colors/metrics sampled from the LIVE popup on syfakb2b.demod4u.gr via
// getComputedStyle (2026-07-27): header #0C6233 · panel rgba(12,98,51,.05) ·
// stepper/button fill rgba(12,98,51,.1) · link #64B773 · body #333 14px/22px ·
// controls 45px tall, square corners, 20px/700 white header title.
//
// Portalled into the SearchModal's <dialog> (useModalLayer), NOT rendered inside
// the ProductCard: inside the card it sat under the results scroll container,
// which on iPhone confined the overlay — the popup opened under the search bar
// with its × out of reach (syfak, 2026-10-27; the home grid's container did not
// do it, which is why offers looked fine there). See modalLayer.ts. Uses
// fetchOfferHtml so a card-hover prefetch makes the open instant.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useModalLayer } from './modalLayer';
import { fetchOfferHtml } from '@/lib/offers/offerHtml';
import { flyToCart } from '@/lib/cart/flyToCart';
import { useClientConfig } from '@/lib/clients/ClientContext';
import { ADDED_LABEL } from './ProductGrid/SyfakProductCard';

interface ParsedOffer {
  /** Offer name incl. the «| Κανόνας : …» part — everything before «Βασική Έκπτωση». */
  title: string;
  /** «Βασική Έκπτωση Προσφοράς : 0,00 %» — verbatim, kept even at zero (their popup shows it). */
  baseLine: string | null;
  /** «Από ποσότητα N Έκπτωση X %» lines — verbatim, in SOURCE order (their popup does not sort). */
  tierLines: string[];
  /** Every «Έκπτωση Bonus : X %» occurrence — their popup renders them all, zeros included. */
  bonusLines: string[];
  /** The payment <strong> verbatim, e.g. «Τρόπος Πληρωμής Προσφοράς: 90 ΗΜΕΡΕΣ -» (their trailing dash and all). */
  paymentLine: string;
  seeFull: { text: string; href: string } | null;
  actionInner: string;
  hasAction: boolean;
}

function resolveHref(href: string | null): string {
  if (!href || href === '#') return '#';
  if (/^https?:\/\//.test(href) || href.startsWith('/')) return href;
  return '/' + href;
}

/**
 * Pull EVERY offer out of the host's markup. A product can sit in several
 * offers at once for the same pharmacy (live 2026-10-19: ~1 in 5 offer
 * products, e.g. FREZYDERM DERMIOX 38329 → offers 8841 + 7310), and the host
 * sends them as ONE div.offer-modal holding one div.form-group PER OFFER, each
 * with its own descr, payment line, link, stepper and add button. The add
 * button carries its own offer id (`prosfores.add(pid, qty, <offerId>)`), so
 * each offer keeps its OWN action block. Reading only the first group is the
 * bug syfak reported 2026-10-19 — the second offer silently vanished.
 *
 * No .form-group at all (markup change) → the whole snippet is parsed as one
 * offer, i.e. the pre-2026-10-19 behaviour.
 */
function parseOffers(html: string): ParsedOffer[] | null {
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const groups = [...doc.querySelectorAll('.form-group')].filter((g) => g.querySelector('.offer-descr'));
    if (groups.length === 0) {
      const single = parseOffer(doc.body, html);
      return single ? [single] : null;
    }
    const offers = groups.map((g) => parseOffer(g, g.outerHTML));
    return offers.every((o): o is ParsedOffer => o !== null) ? offers : null;
  } catch {
    return null;
  }
}

/** Pull ONE offer's display lines out of its markup (`root` = its
 *  div.form-group) VERBATIM (no reformatting — the goal is pixel-parity with
 *  their own popup), and keep its real (functional) stepper + add-to-cart
 *  untouched. */
function parseOffer(root: ParentNode, html: string): ParsedOffer | null {
  try {
    const descr = (root.querySelector('.offer-descr')?.textContent || '').replace(/\s+/g, ' ').trim();

    const stop = descr.search(/Βασική Έκπτωση|Κλίμακες|Από ποσότητα|Έκπτωση Bonus/i);
    const title = (stop === -1 ? descr : descr.slice(0, stop)).trim();

    // Everything from «Βασική Έκπτωση» up to the next section — NOT just up to
    // the first «%». A price-change offer reads «Βασική Έκπτωση Προσφοράς :
    // 0,00 % , Αλλαγή τιμής σε : 11,30»; stopping at the «%» showed "0%" and
    // hid the €11,30 that IS the offer (syfak-reported 2026-10-19). Plain
    // discount offers end at the «%» anyway, so they read exactly as before.
    const baseLine = descr.match(/Βασική Έκπτωση.*?(?=\s*(?:Κλίμακες|Από ποσότητα|Έκπτωση Bonus)|$)/i)?.[0].trim() || null;
    const tierLines = [...descr.matchAll(/Από ποσότητα\s*\d+\s*Έκπτωση\s*[\d.,]+\s*%/gi)].map((m) => m[0].trim());
    const bonusLines = [...descr.matchAll(/Έκπτωση Bonus\s*:\s*[\d.,]+\s*%/gi)].map((m) => m[0].trim());

    const paymentLine = [...root.querySelectorAll('strong')]
      .map((s) => (s.textContent || '').replace(/\s+/g, ' ').trim())
      .find((t) => /Τρόπος Πληρωμής/i.test(t)) || '';

    // «ΔΕΙΤΕ ΟΛΗ ΤΗΝ ΠΡΟΣΦΟΡΑ» — their link navigates (same window) to the full
    // offer page: index.php?route=extension/module/product_price_list&list_id=N
    // (destination verified by clicking it on their live popup, 2026-07-27).
    // Depending on their markup the URL may sit in the anchor's href OR inside
    // inline JS — a bare `href="#"` anchor was rendering as a DEAD link
    // (founder-reported). Resolve it from wherever it lives; if no real URL can
    // be found, render no link at all — hidden beats dead.
    const linkEl = root.querySelector('a');
    let seeHref = linkEl?.getAttribute('href') || null;
    if (!seeHref || seeHref === '#') {
      const m =
        (linkEl?.outerHTML || '').match(/index\.php\?[^"'\s<>]*product_price_list[^"'\s<>]*/i) ||
        html.match(/index\.php\?[^"'\s<>]*product_price_list[^"'\s<>]*/i);
      seeHref = m ? m[0].replace(/&amp;/g, '&') : null;
    }
    const seeFull = linkEl && seeHref
      ? { text: (linkEl.textContent || '').replace(/\s+/g, ' ').trim(), href: seeHref }
      : null;

    const stepper = root.querySelector('.stepper_buttons');
    const button = root.querySelector('.button-cart');
    const actionInner = (stepper?.outerHTML || '') + (button?.outerHTML || '');

    return { title, baseLine, tierLines, bonusLines, paymentLine, seeFull, actionInner, hasAction: !!(stepper && button) };
  } catch {
    return null;
  }
}

const OFFER_CSS = `
.offer-head{background:#0C6233;padding:11px 48px;text-align:center;position:relative}
.offer-head-title{color:#fff;font-size:20px;font-weight:700;line-height:26px}
.offer-close{position:absolute;right:10px;top:50%;transform:translateY(-50%);width:32px;height:32px;display:flex;align-items:center;justify-content:center;color:#fff;font-size:24px;line-height:1;background:none;border:none;cursor:pointer;padding:0}
.offer-close:hover{opacity:.75}
.offer-html{font-size:14px;color:#333;line-height:22px}
/* NB every rule below is .offer-html-prefixed. A bare .offer-payment loses to
   .offer-html p on specificity (0,1,0 vs 0,1,1), which silently swallowed the
   blank line above «Τρόπος Πληρωμής» — founder spotted it against their popup. */
.offer-html p{margin:0}
/* Generous side padding: their panel sits ~40px in from the modal edge. Ours is
   this padding + the 10px white gutter around the panel. */
.offer-panel{background:rgba(12,98,51,0.05);padding:24px 30px 26px}
/* Several offers: separate tinted boxes with the same white 10px gap as the
   gutter, like the stacked boxes on their product page. */
.offer-panel + .offer-panel{margin-top:10px}
.offer-html .offer-gap{height:22px}
.offer-html .offer-payment{color:#0C6233;font-weight:700;margin-top:22px}
.offer-html .offer-see{display:flex;align-items:center;gap:8px;width:fit-content;color:#64B773;font-weight:700;margin-top:6px;text-decoration:none}
.offer-html .offer-see:hover{text-decoration:underline}
/* Navigating state: their offer page takes ~3-4s to answer, and until it does
   the browser shows nothing at all — the founder read it as a broken link.
   Acknowledge the click immediately instead. Pointer events off so a second,
   impatient click can't fire a second navigation. */
.offer-html .offer-see.is-navigating{opacity:.65;pointer-events:none;text-decoration:none}
.offer-html .offer-see-spin{width:13px;height:13px;flex-shrink:0;border-radius:50%;border:2px solid rgba(100,183,115,0.3);border-top-color:#64B773;animation:offerspin .8s linear infinite}
.offer-html .offer-descr-fallback{line-height:1.7}
.offer-html .offer-action{display:flex;align-items:stretch;gap:18px;margin-top:30px}
.offer-html .stepper_buttons{display:inline-flex;align-items:stretch;flex-shrink:0;background:rgba(12,98,51,0.1)}
.offer-html .ctm-stepper{width:44px;height:45px;display:flex;align-items:center;justify-content:center;background:transparent;border:none;font-size:18px;line-height:1;color:#222;cursor:pointer;user-select:none;padding:0}
.offer-html .ctm-stepper:hover{background:rgba(12,98,51,0.08)}
.offer-html .ctm-stepper.minus::before{content:"−"}
.offer-html .ctm-stepper.plus::before{content:"+"}
.offer-html .input_qtycat{width:42px;height:45px;text-align:center;border:none;background:#fff;font-size:14px;font-weight:700;color:#222;-moz-appearance:textfield}
@media (pointer:coarse){.offer-html .input_qtycat{font-size:16px}}
.offer-html .input_qtycat::-webkit-outer-spin-button,.offer-html .input_qtycat::-webkit-inner-spin-button{-webkit-appearance:none;margin:0}
.offer-html .button-cart{flex:1;height:45px;background:rgba(12,98,51,0.1);color:#222;border:none;border-radius:0;font-size:14px;font-weight:700;cursor:pointer;transition:background .15s,color .15s}
.offer-html .button-cart:hover{background:rgba(12,98,51,0.16)}
/* Post-add confirmation, mirroring the cards' «✓ Προστέθηκε» flip. Solid green
   so it reads as a state change, not just a hover. pointer-events off blocks a
   double-add in the moment before the popup closes itself. */
.offer-html .button-cart.offer-added{background:#0C6233;color:#fff;pointer-events:none}
/* Self-contained spinner: keyframes live INSIDE this <style> tag, which renders
   wherever the popup renders — the previous Tailwind animate-spin utility's
   keyframes didn't reach the embed's Shadow DOM, so the ring sat frozen. */
.offer-spinner{width:32px;height:32px;border-radius:50%;border:3px solid rgba(12,98,51,0.15);border-top-color:#0C6233;animation:offerspin .8s linear infinite}
@keyframes offerspin{to{transform:rotate(360deg)}}
`;

interface OfferPopupProps {
  offerUrl: string;
  title?: string;
  onClose: () => void;
  /** Fired when the host's add button inside the popup is clicked (their
   *  prosfores.add has already run by then). Carries the stepper quantity —
   *  the caller logs the add_to_cart analytics event. */
  onCartAdd?: (quantity: number) => void;
}

export default function OfferPopup({ offerUrl, title, onClose, onCartAdd }: OfferPopupProps) {
  const clientConfig = useClientConfig();
  // The modal's dialog — the popup portals there (see the file header).
  const layer = useModalLayer();
  const [state, setState] = useState<{ loading: boolean; error: boolean; html: string | null }>({
    loading: true,
    error: false,
    html: null,
  });
  // «ΔΕΙΤΕ ΟΛΗ ΤΗΝ ΠΡΟΣΦΟΡΑ» navigates to the host's own offer page, which took
  // 3-4 seconds to respond with NOTHING happening on screen in between — the
  // founder's read was "the click did nothing / the link is broken". We can't
  // make their page faster, so acknowledge the click instantly instead.
  // Holds the INDEX of the offer whose link was clicked — with several offers
  // on screen, only that one's link shows the spinner.
  const [navigatingIdx, setNavigatingIdx] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, error: false, html: null });
    fetchOfferHtml(offerUrl)
      .then((html) => {
        if (!cancelled) setState({ loading: false, error: false, html });
      })
      .catch(() => {
        if (!cancelled) setState({ loading: false, error: true, html: null });
      });
    return () => {
      cancelled = true;
    };
  }, [offerUrl]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const offers = useMemo(() => (state.html ? parseOffers(state.html) : null), [state.html]);
  // Structured only when EVERY offer parsed with its action block; otherwise the
  // raw-snippet fallback below shows the host's markup as-is (nothing dropped).
  const structured = !!offers && offers.length > 0 && offers.every((o) => o.hasAction);

  // Their add button is host markup (render-and-trust), so we listen on the
  // wrapper: inline handlers fire first, meaning their prosfores.add has already
  // read the qty and fired its request by the time this bubbles. We add the
  // widget-side feedback the founder asked for (2026-07-27): a ghost flying to
  // the host's cart icon, then CLOSE the popup — the host header (cart badge)
  // is visible above our modal, so the pharmacist sees the count tick up.
  const handleActionClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const btn = (e.target as HTMLElement).closest('.button-cart');
      if (!btn) return; // stepper +/- clicks etc. — not an add
      const qtyRaw = (e.currentTarget.querySelector('.input_qtycat') as HTMLInputElement | null)?.value;
      const quantity = Math.max(1, parseInt(qtyRaw || '1', 10) || 1);
      flyToCart(btn as HTMLElement, clientConfig.cart?.flyToCartSelector);
      onCartAdd?.(quantity);
      // Confirm IN PLACE first (founder: match the cards' «✓ Προστέθηκε»), then
      // close — so the add is acknowledged before the popup vanishes, and the
      // pharmacist's eye lands on the host cart badge afterwards. Safe to mutate
      // this host-rendered button: it unmounts with the popup a beat later.
      btn.textContent = ADDED_LABEL;
      btn.classList.add('offer-added');
      window.setTimeout(onClose, 900);
    },
    [clientConfig.cart?.flyToCartSelector, onCartAdd, onClose],
  );

  // Inside the dialog the overlay is `absolute inset-0`: the dialog is sized to
  // the VISIBLE viewport (SearchModal), so this covers exactly what the shopper
  // sees, keyboard or not. `fixed` only for the no-modal fallback.
  const popup = (
    <div
      className={`${layer ? 'absolute' : 'fixed'} inset-0 z-[100] flex items-center justify-center p-4`}
      style={{ background: 'rgba(0,0,0,0.45)' }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title ? `Προσφορές — ${title}` : 'Προσφορές'}
    >
      <style>{OFFER_CSS}</style>
      <div
        className="relative bg-white shadow-xl w-full max-w-[560px] max-h-[85%] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — their green band, centered white title, × at the right */}
        <div className="offer-head sticky top-0 z-10 font-gotham">
          {/* «(N)» only with 2+ offers: the panels scroll inside the popup and
              macOS hides the scrollbar, so this is the cue that more sit below. */}
          <span className="offer-head-title">
            {structured && offers!.length > 1 ? `Προσφορές (${offers!.length})` : 'Προσφορές'}
          </span>
          <button type="button" className="offer-close" onClick={onClose} aria-label="Κλείσιμο">
            ×
          </button>
        </div>

        {/* Body — white gutter around the tinted panel(s): ONE panel while
            loading / on error / in the raw fallback, and ONE PER OFFER once
            parsed, stacked in the host's order like their product page. */}
        <div className="p-[10px]">
          {!structured && (
            <div className="offer-panel offer-html font-gotham">
              {state.loading && (
                <div className="flex items-center justify-center py-14">
                  <div className="offer-spinner" aria-label="Φόρτωση προσφοράς" />
                </div>
              )}

              {state.error && (
                <p className="py-8 text-center" style={{ color: '#666' }}>
                  Η προσφορά δεν είναι διαθέσιμη αυτή τη στιγμή.
                </p>
              )}

              {/* Fallback: couldn't parse — render the raw snippet, styled. */}
              {state.html != null && (
                <div className="offer-descr-fallback" dangerouslySetInnerHTML={{ __html: state.html }} />
              )}
            </div>
          )}

          {structured && offers!.map((offer, idx) => (
            <div key={idx} className="offer-panel offer-html font-gotham">
              {offer.title && <p>{offer.title}</p>}

              {offer.baseLine && (
                <>
                  <div className="offer-gap" />
                  <p>{offer.baseLine}</p>
                </>
              )}

              {offer.tierLines.length > 0 && (
                <>
                  <div className="offer-gap" />
                  <p>Κλίμακες</p>
                  {offer.tierLines.map((line, i) => (
                    <p key={i}>{line}</p>
                  ))}
                </>
              )}

              {offer.bonusLines.map((line, i) => (
                <p key={i}>{line}</p>
              ))}

              {offer.paymentLine && <p className="offer-payment">{offer.paymentLine}</p>}

              {offer.seeFull && offer.seeFull.text && (
                // Opens in the same window, matching ui.productLinkTarget —
                // syfak deliberately sets '_self' for product links, so the
                // offer link follows the same convention rather than
                // inventing a different one.
                <a
                  className={`offer-see${navigatingIdx === idx ? ' is-navigating' : ''}`}
                  href={resolveHref(offer.seeFull.href)}
                  target={clientConfig.ui.productLinkTarget ?? '_blank'}
                  rel={(clientConfig.ui.productLinkTarget ?? '_blank') === '_blank' ? 'noopener noreferrer' : undefined}
                  onClick={() => setNavigatingIdx(idx)}
                  aria-busy={navigatingIdx === idx || undefined}
                >
                  {navigatingIdx === idx && <span className="offer-see-spin" aria-hidden />}
                  {offer.seeFull.text}
                </a>
              )}

              {/* THIS offer's own stepper + add-to-cart, verbatim (functional):
                  its button carries this offer's id, and their inline handlers
                  only look inside this block, so each offer adds under itself.
                  onClick = OUR feedback layer (ghost + close), fired AFTER their
                  inline prosfores.add has run. */}
              <div className="offer-action" onClick={handleActionClick} dangerouslySetInnerHTML={{ __html: offer.actionInner }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return layer ? createPortal(popup, layer) : popup;
}
